import React, { useEffect, useState } from "react";
import { T, statusColor, statusFill } from "../theme/tokens.js";
import { Panel, Btn, Chip } from "./ui/index.jsx";
import { riskScore, effectiveDueDate, activityHealth } from "../lib/calc.js";
import { fmtMoney, fmtDate } from "../lib/format.js";

/* Drill down.
 *
 * A number nobody can open is a number nobody can act on. "33 overdue" raises
 * the question rather than answering it, so every bar, dot, card and slice in
 * this app carries the records it was counted from and a click opens them
 * where you are standing.
 *
 * This lives on its own rather than inside the dashboard because the schedule,
 * the finance page and a saved tracker view all owe the same thing, and three
 * versions of one panel is how three of them end up different.
 *
 * It floats over the page rather than sitting in it. That is not decoration.
 * It sat at the top of the dashboard, and the dashboard is six charts long, so
 * clicking a bar near the bottom opened the records a full screen above where
 * you were looking and the click read as broken. Over the page, the records
 * appear where the click happened, whatever the page is and however far down
 * it you are, and closing puts you back exactly where you were.
 *
 * Columns are chosen per register, because the useful five for a deliverable
 * are not the useful five for an invoice. A tracker has no fixed columns at
 * all, so that caller passes its own.
 */
export const DRILL_COLUMNS = {
  deliverables: [
    ["Deliverable", (r) => r.title],
    ["Vendor", (r) => r.vendor || ""],
    ["Stage", (r) => r.stage || ""],
    ["Type", (r) => r.deliverable_type || ""],
    ["Due", (r) => fmtDate(effectiveDueDate(r))],
    ["Status", (r) => r.status || ""],
  ],
  risks: [
    ["Risk", (r) => r.title],
    ["Reference", (r) => r.reference || ""],
    ["Workstream", (r) => r.workstream || ""],
    ["Score", (r) => (r.likelihood && r.impact ? riskScore(r) : (r.rating || ""))],
    ["Owner", (r) => r.owner || ""],
    ["Status", (r) => r.status || ""],
  ],
  permits: [
    ["Permit", (r) => r.title],
    ["School or site", (r) => r.site || ""],
    ["Agency", (r) => r.issuing_agency || ""],
    ["Submitted", (r) => fmtDate(r.submitted)],
    ["Status", (r) => r.status || ""],
  ],
  invoices: [
    ["Invoice", (r) => r.title],
    ["Vendor", (r) => r.vendor || ""],
    ["Submitted", (r) => fmtDate(r.submitted)],
    ["Terms", (r) => (r._aging?.termsDays ? `Net ${r._aging.termsDays}` : "")],
    ["Days past", (r) => (r._aging?.pastDays ? r._aging.pastDays : "")],
    ["Amount", (r) => fmtMoney(r.amount)],
  ],
  /* A schedule activity is judged on where it sits against its own dates, so
     the state is computed rather than read off a status column the export
     never had. */
  schedule_stages: [
    ["Activity", (r) => r.title],
    ["WBS", (r) => r.wbs || ""],
    ["Starts", (r) => fmtDate(r.start_date)],
    ["Finishes", (r) => fmtDate(r.finish_date)],
    ["Complete", (r) => (r.percent_complete === null || r.percent_complete === undefined
      ? "" : `${r.percent_complete}%`)],
    ["State", (r) => {
      const h = activityHealth(r);
      return h.charAt(0).toUpperCase() + h.slice(1);
    }],
  ],
  expenses: [
    ["Expense", (r) => r.title],
    ["Category", (r) => r.category || ""],
    ["Date", (r) => fmtDate(r.date)],
    ["Amount", (r) => fmtMoney(r.amount)],
  ],
  resources: [
    ["Person", (r) => r.title],
    ["Role", (r) => r.role || ""],
    ["Rate", (r) => fmtMoney(r.rate)],
    ["Hours planned", (r) => r.hours_planned ?? ""],
    ["Hours used", (r) => r.hours_used ?? ""],
  ],
  compliance_items: [
    ["Requirement", (r) => r.title],
    ["Source", (r) => r.source || ""],
    ["Owner", (r) => r.owner || ""],
    ["Status", (r) => r.status || ""],
  ],
  milestones: [
    ["Milestone", (r) => r.title],
    ["Date", (r) => fmtDate(r.date)],
    ["Owner", (r) => r.owner || ""],
    ["Status", (r) => r.status || ""],
  ],
};

/* Which columns to show. A caller with its own shape hands them over; a
   register is looked up; anything unrecognised falls back rather than
   throwing, because an empty panel is worse than an approximate one. */
const columnsFor = (drill) =>
  drill.columns ?? DRILL_COLUMNS[drill.kind] ?? DRILL_COLUMNS.deliverables;


/* One record reads down the page, not across it.
 *
 * Clicking a school on a map opens exactly one row, and a row with thirty
 * columns laid out as a table is a sideways scroll nobody can read. The same
 * fields stacked as label and value need no scrolling at all, and a long
 * value has the whole width of the panel to sit in.
 */
function Record({ cols, row }) {
  return (
    <div style={{ display: "grid", gap: 0 }}>
      {cols.map(([label, get]) => {
        const v = get(row);
        const text = v === null || v === undefined ? "" : String(v);
        const chip = text.length <= 22 && statusFill(text) !== null;
        return (
          <div key={label} style={{
            display: "grid", gridTemplateColumns: "minmax(140px, 210px) 1fr",
            gap: 14, padding: "8px 2px", borderBottom: `1px solid ${T.line}`,
            alignItems: "baseline",
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.06em", color: T.navy,
            }}>{label}</div>
            <div style={{ fontSize: 13, color: text ? T.ink : T.soft,
                          overflowWrap: "break-word", lineHeight: 1.6 }}>
              {!text
                ? "Not recorded"
                : chip
                  ? <Chip color={statusColor(text)}>{text}</Chip>
                  : text}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DrillPanel({ drill, onClose }) {
  const [query, setQuery] = useState("");
  /* Which of several records is open in full. A table is the right shape for
     comparing rows and the wrong shape for reading one, so the table stays a
     summary and a click opens the whole record. */
  const [opened, setOpened] = useState(null);

  /* A new set of records starts unfiltered. Otherwise a filter typed for the
     last one silently hides most of the next one. */
  useEffect(() => { setQuery(""); setOpened(null); }, [drill?.title, drill?.rows]);

  useEffect(() => {
    if (!drill) return undefined;
    const key = (e) => { if (e.key === "Escape") onClose(); };
    /* The page behind must not scroll under the records. */
    const held = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", key);
    return () => {
      document.body.style.overflow = held;
      window.removeEventListener("keydown", key);
    };
  }, [drill, onClose]);

  if (!drill) return null;
  const cols = columnsFor(drill);

  const q = query.trim().toLowerCase();
  const rows = q
    ? drill.rows.filter((r) => cols.some(([, get]) =>
        String(get(r) ?? "").toLowerCase().includes(q)))
    : drill.rows;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 70, background: "rgba(20,40,75,0.45)",
        overflowY: "auto", padding: "40px 16px 24px",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
      }}
    >
    <div onClick={(e) => e.stopPropagation()}
      style={{ width: "100%", maxWidth: 1100,
               boxShadow: "0 18px 50px rgba(20,40,75,0.35)", borderRadius: 4 }}>
    <Panel
      title={drill.title}
      accent={T.gold}
      right={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter"
            style={{ padding: "5px 9px", border: `1px solid ${T.line}`, borderRadius: 4,
                     fontSize: 13, background: "#fff", color: T.ink, width: 150 }} />
          <Btn small kind="ghost" onClick={onClose}>Close</Btn>
        </div>
      }
    >
      <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 8 }}>
        {rows.length}{rows.length !== drill.rows.length ? ` of ${drill.rows.length}` : ""} record
        {rows.length === 1 ? "" : "s"} behind that number.
        {rows.length > 1 && opened === null ? " Click a row to read it in full." : ""}
        {" "}Escape closes this.
      </div>
      {(rows.length === 1 || opened !== null) ? (
        <div style={{ maxHeight: 460, overflowY: "auto" }}>
          {opened !== null && (
            <div style={{ marginBottom: 10 }}>
              <Btn small kind="ghost" onClick={() => setOpened(null)}>
                Back to all {rows.length} records
              </Btn>
            </div>
          )}
          <Record cols={cols} row={opened !== null ? opened : rows[0]} />
        </div>
      ) : (
      <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr>{cols.map(([h]) => (
            <th key={h} style={{
              position: "sticky", top: 0, background: T.panel, textAlign: "left",
              padding: "7px 10px", borderBottom: `2px solid ${T.navy}`, color: T.navy,
              fontSize: 11, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.06em", whiteSpace: "nowrap",
            }}>{h}</th>
          ))}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id ?? i} onClick={() => setOpened(r)}
              title="Open this record in full"
              style={{ borderBottom: `1px solid ${T.line}`, cursor: "pointer" }}>
              {cols.map(([h, get], j) => {
                const v = get(r);
                const text = v === null || v === undefined ? "" : String(v);
                /* A state reads as a state here too, in the same four colours
                   it carries everywhere else. */
                const chip = j > 0 && text.length <= 22 && statusFill(text) !== null;
                return (
                  <td key={h} style={{ padding: "6px 10px", verticalAlign: "top" }}>
                    {/* Each value sits in a box it cannot escape, with a floor
                        as well as a ceiling. A ceiling on its own is what
                        squeezed the risk numbers down to one fragment per
                        line: thirty columns competing for the width, and
                        nothing saying how narrow a column may get. The table
                        scrolls sideways instead, which is the honest answer
                        when there are genuinely thirty columns.

                        Long text is cut off after three lines rather than
                        given its own scrollbar, because a panel full of tiny
                        scrollbars is worse to read than one that is plainly
                        truncated. The whole value is on hover, and clicking
                        the row opens the record in full. */}
                    <div title={text} style={chip ? { whiteSpace: "nowrap" } : {
                      minWidth: j === 0 ? 150 : 110,
                      maxWidth: j === 0 ? 340 : 260,
                      /* break-word, not anywhere. "anywhere" also tells the
                         browser a column may legitimately be one character
                         wide, which is what turned Public into Publi/c and
                         split the coordinates down the middle. This breaks a
                         word only when it genuinely will not fit. */
                      overflowWrap: "break-word",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}>
                      {chip ? <Chip color={statusColor(text)}>{text}</Chip> : text}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}</tbody>
        </table>
      </div>
      )}
    </Panel>
    </div>
    </div>
  );
}
